import type { PodcastSearchResult, Subscription } from "@/types/podcast";
import PodcastCard from "./PodcastCard";
import { Skeleton } from "@/components/ui/skeleton";

interface PodcastGridProps {
  podcasts: (PodcastSearchResult | Subscription)[];
  isLoading?: boolean;
  /** Display as horizontally scrollable two-row carousel */
  horizontal?: boolean;
}

export default function PodcastGrid({ podcasts, isLoading, horizontal }: PodcastGridProps) {
  if (isLoading) {
    if (horizontal) {
      return (
        <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
          <div className="grid grid-rows-2 grid-flow-col gap-3 auto-cols-[140px] sm:auto-cols-[160px]">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex flex-col rounded-lg border border-gray-200 bg-white p-2">
                <Skeleton className="mb-2 aspect-square w-full rounded-md" />
                <Skeleton className="mb-1 h-3 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex flex-col rounded-lg border border-gray-200 bg-white p-3">
            <Skeleton className="mb-3 aspect-square w-full rounded-md" />
            <Skeleton className="mb-1.5 h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (podcasts.length === 0) return null;

  if (horizontal) {
    return (
      <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <div className="grid grid-rows-2 grid-flow-col gap-3 auto-cols-[140px] sm:auto-cols-[160px]">
          {podcasts.map((podcast, i) => (
            <PodcastCard key={podcast.feed_url ?? i} podcast={podcast} compact />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {podcasts.map((podcast, i) => (
        <PodcastCard key={podcast.feed_url ?? i} podcast={podcast} />
      ))}
    </div>
  );
}
